import React from 'react';
import { isValidPostalCode } from '@express-labs/raven-tools';

import Button from '../Button/Button';

import s from './LocationChanger.scss';

const ERRMSG_INVALID = 'Please enter a valid postal code.';
const ERRMSG_SAME =
  'This is already your current postal code. No change is necessary.';

export interface LocationChangerProps {
  className?: string;
  onLocationChange: (inputValue: string) => void;
  postalCode: string;
  alwaysEnabled?: boolean;
}

interface LocationChangerState {
  hasError: boolean;
  inputValue: string;
  errorMsg?: string;
}

interface LocationChangerComps {
  zipField?: React.RefObject<HTMLInputElement>;
}

export default class LocationChanger extends React.Component<
  LocationChangerProps,
  LocationChangerState
> {
  static displayName = 'LocationChanger';

  static defaultProps: Partial<LocationChangerProps> = {
    alwaysEnabled: false,
  };

  comps: LocationChangerComps;

  constructor(props: LocationChangerProps) {
    super(props);

    this.state = {
      hasError: false,
      inputValue: '',
    };

    this.comps = {
      zipField: undefined,
    };

    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.handleFormSubmitKeyPress = this.handleFormSubmitKeyPress.bind(this);
    this.captureInput = this.captureInput.bind(this);
  }

  onFormSubmit(e: React.FormEvent) {
    const { onLocationChange, postalCode } = this.props;
    const { inputValue } = this.state;
    const isValid = isValidPostalCode(inputValue);

    e.preventDefault();

    // start fresh
    this.setState({ hasError: false });

    // check for errors
    if (isValid) {
      if (inputValue === postalCode) {
        this.setState({
          hasError: true,
          errorMsg: ERRMSG_SAME,
        });
      } else {
        if (typeof onLocationChange === 'function')
          onLocationChange(inputValue);
      }
    } else {
      this.setState({
        hasError: true,
        errorMsg: ERRMSG_INVALID,
      });
    }
  }

  handleFormSubmitKeyPress(e: React.KeyboardEvent) {
    if (!['Enter', ' '].includes(e.key)) return undefined;
    return this.onFormSubmit(e);
  }

  captureInput(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ inputValue: e.target.value });
  }

  render() {
    const { hasError, errorMsg, inputValue } = this.state;
    const { alwaysEnabled, className: propsClassName } = this.props;
    const className = [
      'locationChanger',
      s.locationChangerContainer,
      propsClassName,
    ]
      .join(' ')
      .trim();

    return (
      <form
        onSubmit={this.onFormSubmit}
        className={className}
        data-testid="locationChanger__form"
      >
        <div>
          <label
            className={`labelActive ${s.zipLabel}`}
            htmlFor="postalCode"
            data-testid="locationChanger__label"
          >
            Postal Code
          </label>
          {hasError && (
            <span
              className={`labelError ${s.postalCodeError}`}
              role="alert"
              data-testid="locationChanger__error"
            >
              {errorMsg}
            </span>
          )}
          <input
            type="text"
            className={s.zipInput}
            name="postalCode"
            id="postalCode"
            ref={this.comps.zipField}
            placeholder="Enter Postal Code"
            onChange={this.captureInput}
            value={inputValue}
            aria-required="true"
            aria-invalid={hasError ? 'true' : 'false'}
            required
            data-testid="locationChanger__input"
          />
          <Button
            as="button"
            mode="solid"
            theme="dark"
            size="small"
            onClick={this.onFormSubmit}
            onKeyDown={this.handleFormSubmitKeyPress}
            disabled={inputValue === '' && !alwaysEnabled}
            data-testid="locationChanger__button"
          >
            CHANGE
          </Button>
        </div>
      </form>
    );
  }
}
